import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { TeamMeasures } from '../../../api/workEnvironmentTypes';
import { usePickMeasuresMutation } from '../../../hooks/mutations/useTeamMeasuresMutations';
import { proposalsByArea, togglePick } from '../../WorkEnvironmentSurveyDetail/measuresView';
import { areaName, LEVEL_COLORS } from '../../WorkEnvironmentSurveyDetail/riskView';
import form from '../../FormSubmission/WorkEnvironmentFormSubmission.module.css';
import classes from '../WorkEnvironmentTeamMeasures.module.css';

type Props = { uuid: string; token: string; measures: TeamMeasures };

/** Anonymous. A person picks up to three, and can change them until the deadline. */
export const PickForm = ({ uuid, token, measures }: Props): ReactElement => {
  const { t } = useTranslation();
  const [picks, setPicks] = useState<number[]>(measures.picks);
  const [full, setFull] = useState(false);
  const [sent, setSent] = useState(false);
  const pick = usePickMeasuresMutation(uuid, token);
  const max = measures.maxHighlights;

  const toggle = (id: number) => {
    const next = togglePick(picks, id, max);
    setFull(next === picks && !picks.includes(id));
    setPicks(next);
    setSent(false);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    pick.mutate(picks, { onSuccess: () => setSent(true) });
  };

  return (
    <form onSubmit={submit} noValidate className={form.form}>
      <Alert data-color="info" className={classes.warning}>
        <Heading level={2} data-size="xs">
          {t('workEnvironmentMeasures.team.notAutomaticTitle')}
        </Heading>
        <Paragraph data-size="sm">
          {t(`workEnvironmentMeasures.team.${measures.hasSafetyRep ? 'notAutomaticText' : 'notAutomaticTextNoRep'}`)}
        </Paragraph>
      </Alert>
      <div className={classes.group}>
        <Heading level={2} data-size="xs">
          {t('workEnvironmentMeasures.team.pickTitle', { count: max })}
        </Heading>
        <Paragraph data-size="sm" className={form.subtle}>
          {t('workEnvironmentMeasures.team.pickHelp')}
        </Paragraph>
      </div>
      {proposalsByArea(measures.areas, measures.proposals).map(({ area, proposals }) => (
        <div key={area.key} className={classes.group}>
          <div className={classes.choiceMeta}>
            <Heading level={3} data-size="2xs">
              {areaName(area, t)}
            </Heading>
            <Tag data-size="sm" data-color={LEVEL_COLORS[area.level]}>
              {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
            </Tag>
          </div>
          <ul className={classes.choices}>
            {proposals.map((proposal) => (
              <li key={proposal.id}>
                <label className={classes.choice}>
                  <input
                    type="checkbox"
                    className="ds-input"
                    checked={picks.includes(proposal.id)}
                    onChange={() => toggle(proposal.id)}
                  />
                  <span className={classes.choiceText}>
                    <span className="ds-label">{proposal.title}</span>
                    {proposal.reason && (
                      <span className="ds-paragraph" data-size="sm">
                        {proposal.reason}
                      </span>
                    )}
                    {proposal.fromLeader && (
                      <span className={`ds-paragraph ${form.subtle}`} data-size="xs">
                        {t('workEnvironmentMeasures.team.fromLeader')}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <div className={classes.picked}>
        <Paragraph data-size="sm" aria-live="polite">
          {full
            ? t('workEnvironmentMeasures.team.full', { count: max })
            : picks.length > 0
              ? t('workEnvironmentMeasures.team.picked', { count: picks.length, max })
              : t('workEnvironmentMeasures.team.noneYet')}
        </Paragraph>
        <Button type="submit" variant="primary" loading={pick.isPending}>
          {t('workEnvironmentMeasures.team.sendPicks')}
        </Button>
      </div>
      {sent && (
        <Alert data-color="success" role="status">
          <Paragraph>{t('workEnvironmentMeasures.team.picksSent')}</Paragraph>
        </Alert>
      )}
      {pick.isError && (
        <Alert data-color="danger">
          <Paragraph>{t('workEnvironmentMeasures.team.picksFailed')}</Paragraph>
        </Alert>
      )}
    </form>
  );
};
